import { ArrowLeft, Camera, CameraOff, Check, ChevronDown, EyeOff, Headphones, LoaderCircle, LockKeyhole, Mic, MicOff, RefreshCw, Shuffle, Signal, SignalLow, WifiOff } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { Brand } from "../components/Brand";
import { CameraOffIdentity } from "../components/CameraOffIdentity";
import { StatusNotice } from "../components/StatusNotice";
import { useAuth } from "../features/auth/AuthContext";
import { useDevicePreview } from "../features/prejoin/useDevicePreview";
import { clearPrejoinTicket, loadPrejoinTicket, savePrejoinTicket, saveRoomSession } from "../features/room/roomSession";
import { api, assertJoinResult, assertRoom, getApiErrorMessage } from "../lib/api";
import { rosterAsset, talkRoster } from "../lib/assets";
import { talkBrand } from "../lib/brand";
import { roleLabel, roomStateLabel, ui } from "../lib/ui";
import { clearGuestIdentityDraft, loadOrCreateGuestIdentityDraft, saveGuestIdentityDraft, shuffleGuestCallsign, type GuestIdentityDraft } from "../lib/guestIdentity";
import { loadRoomHaramMode, saveRoomHaramMode } from "../lib/roomAppearance";
import type { TalkProfile, TalkRoom } from "../lib/types";

const networkCopy = {
  good: { label: ui("Good connection", "Хорошее соединение"), detail: ui("Ready for video and sharing", "Можно включать видео и показ экрана"), icon: Signal },
  limited: { label: ui("Limited connection", "Слабое соединение"), detail: ui(`${talkBrand.productName} will protect audio and frame cadence`, "Звук и частота кадров будут в приоритете."), icon: SignalLow },
  offline: { label: ui("Offline", "Нет сети"), detail: ui("Reconnect before joining", "Подключитесь к сети, прежде чем войти."), icon: WifiOff },
};

function shownMessage(message: string): string {
  const copy: Record<string, string> = {
    "This browser cannot preview camera or microphone. You can still join listen-only.": "Этот браузер не может показать камеру или микрофон. Войти и только слушать всё равно можно.",
    "Camera or microphone permission was denied. Turn both off to join with an avatar, or update browser permissions and try again.": "Доступ к камере или микрофону запрещён. Выключите оба, чтобы войти с фигурой, или разрешите доступ в браузере и попробуйте снова.",
    "Talk could not start the selected devices. Choose another device or join with them off.": "Не удалось включить выбранные устройства. Выберите другие или войдите с выключенными.",
    "Talk took too long to respond. Try again.": "Ответ шёл слишком долго. Попробуйте ещё раз.",
    "The request was cancelled.": "Запрос отменён.",
    "Talk is unreachable. Check your connection and try again.": "Сервер недоступен. Проверьте соединение и попробуйте ещё раз.",
    "Something went wrong. Try again.": "Что-то пошло не так. Попробуйте ещё раз.",
    "The server returned an incomplete room.": "Сервер вернул неполные данные комнаты.",
    "The server did not issue a prejoin ticket.": "Сервер не выдал пропуск в комнату.",
    "The server did not issue complete room credentials.": "Сервер не выдал полные данные для входа в комнату.",
  };
  const translated = copy[message];
  return translated ? ui(message, translated) : message;
}

export function PrejoinPage() {
  const { roomId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const device = useDevicePreview(videoRef);
  const initialRoom = (location.state as { room?: TalkRoom } | null)?.room ?? null;
  const [room, setRoom] = useState<TalkRoom | null>(() => initialRoom);
  const [haramMode, setHaramMode] = useState(() => initialRoom && talkBrand.supportsHaramMode ? loadRoomHaramMode(initialRoom.id, initialRoom.haramModeDefault === true) : false);
  const appearanceRoomId = useRef(initialRoom?.id ?? "");
  const [loadingRoom, setLoadingRoom] = useState(!room);
  const [roomError, setRoomError] = useState("");
  const [displayNameOverride, setDisplayName] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [prejoinTicket, setPrejoinTicket] = useState(() => loadPrejoinTicket(roomId));
  const [accountProfile, setAccountProfile] = useState<{ accountId: string; profile: TalkProfile } | null>(null);
  const [guestIdentity, setGuestIdentity] = useState<GuestIdentityDraft | null>(null);
  const [guestAvatarOpen, setGuestAvatarOpen] = useState(false);
  const accountId = auth.snapshot?.user.id ?? "";
  const displayName = displayNameOverride ?? auth.snapshot?.user.displayName ?? auth.snapshot?.user.handle ?? guestIdentity?.displayName ?? "";

  useEffect(() => {
    if (room || !roomId) return;
    const controller = new AbortController();
    api.rooms.get(roomId, controller.signal).then((next) => setRoom(assertRoom(next))).catch((error) => {
      if (!controller.signal.aborted) setRoomError(getApiErrorMessage(error));
    }).finally(() => { if (!controller.signal.aborted) setLoadingRoom(false); });
    return () => controller.abort();
  }, [room, roomId]);

  useEffect(() => {
    if (!room || appearanceRoomId.current === room.id) return;
    appearanceRoomId.current = room.id;
    setHaramMode(talkBrand.supportsHaramMode ? loadRoomHaramMode(room.id, room.haramModeDefault) : false);
  }, [room]);

  useEffect(() => {
    if (prejoinTicket || auth.loading || !auth.snapshot || !roomId) return;
    const controller = new AbortController();
    api.rooms.accountPrejoin(roomId, controller.signal).then((resolved) => {
      savePrejoinTicket(roomId, resolved.prejoinTicket);
      setPrejoinTicket(resolved.prejoinTicket);
      setRoom(resolved.room);
      setRoomError("");
    }).catch((error) => {
      if (!controller.signal.aborted) setRoomError(getApiErrorMessage(error));
    }).finally(() => { if (!controller.signal.aborted) setLoadingRoom(false); });
    return () => controller.abort();
  }, [auth.loading, auth.snapshot, prejoinTicket, roomId]);

  useEffect(() => {
    if (auth.loading || !accountId) return;
    const controller = new AbortController();
    api.profile.load(controller.signal).then((profile) => {
      if (!controller.signal.aborted) setAccountProfile({ accountId, profile });
    }).catch(() => {
      // A profile lookup must never prevent someone from joining the room.
    });
    return () => controller.abort();
  }, [accountId, auth.loading]);

  useEffect(() => {
    if (auth.loading || auth.snapshot || !roomId) return;
    const timer = window.setTimeout(() => {
      setGuestIdentity((current) => current ?? loadOrCreateGuestIdentityDraft(roomId));
    }, 0);
    return () => window.clearTimeout(timer);
  }, [auth.loading, auth.snapshot, roomId]);

  function updateGuestIdentity(patch: Partial<GuestIdentityDraft>) {
    if (auth.snapshot || !roomId) return;
    setGuestIdentity((current) => {
      const next = { ...(current ?? loadOrCreateGuestIdentityDraft(roomId)), ...patch };
      if (next.displayName.trim()) saveGuestIdentityDraft(roomId, next);
      return next;
    });
  }

  function shuffleGuestName() {
    if (!roomId) return;
    const next = shuffleGuestCallsign(guestIdentity ?? loadOrCreateGuestIdentityDraft(roomId));
    setDisplayName(next.displayName);
    setGuestIdentity(next);
    saveGuestIdentityDraft(roomId, next);
  }

  function updateHaramMode(enabled: boolean) {
    if (!talkBrand.supportsHaramMode) return;
    setHaramMode(enabled);
    if (room) saveRoomHaramMode(room.id, enabled);
  }

  async function testSpeaker() {
    const AudioContextConstructor = window.AudioContext;
    if (!AudioContextConstructor) return;
    const context = new AudioContextConstructor();
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const destination = context.createMediaStreamDestination();
    const audio = new Audio();
    gain.gain.value = 0.05;
    oscillator.frequency.value = 520;
    oscillator.connect(gain).connect(destination);
    audio.srcObject = destination.stream;
    try {
      const sinkAudio = audio as HTMLAudioElement & { setSinkId?: (deviceId: string) => Promise<void> };
      if (device.preferences.speakerDeviceId && sinkAudio.setSinkId) await sinkAudio.setSinkId(device.preferences.speakerDeviceId);
      await audio.play();
      oscillator.start();
      oscillator.stop(context.currentTime + 0.18);
      window.setTimeout(() => { audio.pause(); audio.srcObject = null; void context.close(); }, 350);
    } catch {
      audio.srcObject = null;
      void context.close();
      setJoinError(ui("The selected speaker could not play the test sound. Choose another output or use the system default.", "Выбранный динамик не воспроизвёл тестовый звук. Выберите другой выход или системный по умолчанию."));
    }
  }

  async function join() {
    if (!room || !displayName.trim() || device.network === "offline" || joining) return;
    setJoining(true);
    setJoinError("");
    try {
      if (!prejoinTicket) {
        setJoinError(ui("Open the invitation link again to request fresh room admission.", "Откройте ссылку-приглашение ещё раз, чтобы заново запросить вход в комнату."));
        setJoining(false);
        return;
      }
      const joined = await api.rooms.join(room.id, {
        prejoinTicket,
        displayName: displayName.trim(),
        ...(!auth.snapshot && guestIdentity ? { assetKey: guestIdentity.assetKey } : {}),
      });
      const result = assertJoinResult({ ...joined, room: { ...joined.room, ...(room.code ? { code: room.code } : {}) } });
      clearPrejoinTicket(room.id);
      if (!auth.snapshot) clearGuestIdentityDraft(room.id);
      saveRoomSession({
        result,
        media: {
          cameraEnabled: device.preferences.cameraEnabled && Boolean(device.stream?.getVideoTracks().length),
          microphoneEnabled: device.preferences.microphoneEnabled && Boolean(device.stream?.getAudioTracks().length),
          cameraDeviceId: device.preferences.cameraDeviceId,
          microphoneDeviceId: device.preferences.microphoneDeviceId,
          speakerDeviceId: device.preferences.speakerDeviceId,
          reducedData: device.preferences.reducedData,
          defaultLayout: device.preferences.defaultLayout,
        },
      });
      device.stream?.getTracks().forEach((track) => track.stop());
      navigate(`/r/${encodeURIComponent(room.id)}`, { replace: true });
    } catch (error) {
      setJoinError(getApiErrorMessage(error));
      setJoining(false);
    }
  }

  const network = networkCopy[device.network];
  const NetworkIcon = network.icon;
  const cameraLive = Boolean(device.stream?.getVideoTracks().some((track) => track.enabled));
  const effectiveHaramMode = talkBrand.supportsHaramMode && haramMode;
  const accountAvatar = accountProfile?.accountId === accountId
    ? !effectiveHaramMode && accountProfile.profile.avatarUrl ? accountProfile.profile.avatarUrl : rosterAsset(accountProfile.profile.assetKey, effectiveHaramMode)
    : undefined;
  const previewAvatar = auth.snapshot ? accountAvatar : rosterAsset(guestIdentity?.assetKey, effectiveHaramMode);
  const selectedGuestAvatar = guestIdentity ? talkRoster.find((asset) => asset.key === guestIdentity.assetKey) : undefined;

  return (
    <div className="prejoin-page">
      <header className="prejoin-header"><Brand compact /><Link className="quiet-link" to="/join"><ArrowLeft aria-hidden="true" size={16} />{ui("Use another code", "Другой код")}</Link></header>
      <main id="main-content" className="prejoin-main" tabIndex={-1}>
        <section className="preview-zone" aria-label={ui("Device preview", "Предпросмотр устройств")}>
          <div className="preview-frame">
            {cameraLive ? <video ref={videoRef} muted playsInline className={device.preferences.mirrorPreview ? "is-mirrored" : ""} /> : <CameraOffIdentity large name={displayName || ui("Guest", roleLabel("guest"))} avatarUrl={previewAvatar} guest={!auth.snapshot} />}
            <div className="preview-identity"><strong>{displayName || ui("Your preview", "Ваш предпросмотр")}</strong>{!auth.snapshot ? <span>{ui("Guest", roleLabel("guest"))}</span> : null}</div>
            <div className="preview-controls">
              <button type="button" className={device.preferences.microphoneEnabled ? "" : "is-off"} aria-pressed={device.preferences.microphoneEnabled} aria-label={device.preferences.microphoneEnabled ? ui("Turn microphone off", "Выключить микрофон") : ui("Turn microphone on", "Включить микрофон")} onClick={() => device.update({ microphoneEnabled: !device.preferences.microphoneEnabled }, !device.stream)}>{device.preferences.microphoneEnabled ? <Mic aria-hidden="true" /> : <MicOff aria-hidden="true" />}</button>
              <button type="button" className={device.preferences.cameraEnabled ? "" : "is-off"} aria-pressed={device.preferences.cameraEnabled} aria-label={device.preferences.cameraEnabled ? ui("Turn camera off", "Выключить камеру") : ui("Turn camera on", "Включить камеру")} onClick={() => device.update({ cameraEnabled: !device.preferences.cameraEnabled }, !device.stream)}>{device.preferences.cameraEnabled ? <Camera aria-hidden="true" /> : <CameraOff aria-hidden="true" />}</button>
            </div>
          </div>
          {!device.stream ? <button className="button button--secondary preview-request" type="button" onClick={() => void device.request()} disabled={device.requesting}>{device.requesting ? <LoaderCircle className="spin" aria-hidden="true" size={18} /> : <Camera aria-hidden="true" size={18} />}{device.requesting ? ui("Checking devices…", "Проверяем устройства…") : ui("Check camera and microphone", "Проверить камеру и микрофон")}</button> : null}
          {device.permissionMessage ? <StatusNotice tone="warning" title={ui("Device preview unavailable", "Предпросмотр устройств недоступен")}>{shownMessage(device.permissionMessage)}</StatusNotice> : null}
        </section>

        <section className="prejoin-panel" aria-labelledby="prejoin-title">
          {loadingRoom ? <div className="loading-state"><LoaderCircle className="spin" aria-hidden="true" /><p>{ui("Opening room…", "Открываем комнату…")}</p></div> : roomError ? <StatusNotice tone="danger" title={ui("Room unavailable", "Комната недоступна")}>{shownMessage(roomError)}</StatusNotice> : room ? (
            <>
              <div className="prejoin-panel__title"><span className={`room-state room-state--${room.state}`}>{roomStateLabel(room.state)}</span><h1 id="prejoin-title">{room.title}</h1><p>{ui("Check your name and devices before joining.", "Проверьте имя и устройства перед входом.")}</p></div>
              <div className={`prejoin-name-field${auth.snapshot ? "" : " is-guest"}`}>
                <label className="field"><span>{ui("Display name", "Имя")}</span><input maxLength={64} required autoComplete="name" value={displayName} onChange={(event) => { setDisplayName(event.target.value); updateGuestIdentity({ displayName: event.target.value }); }} /><small>{auth.snapshot ? ui(`Joining as @${auth.snapshot.user.handle}`, `Вы входите как @${auth.snapshot.user.handle}`) : ui("A private callsign is ready. Change it or continue immediately.", "Имя для комнаты уже готово. Измените его или входите сразу.")}</small></label>
                {!auth.snapshot ? <button className="prejoin-name-shuffle" type="button" onClick={shuffleGuestName}><Shuffle aria-hidden="true" /><span>{ui("New callsign", "Другое имя")}</span></button> : null}
              </div>
              {!auth.snapshot && guestIdentity ? (
                <div className="guest-avatar-control">
                  <div className="guest-avatar-summary">
                    <CameraOffIdentity name={displayName || ui("Guest", roleLabel("guest"))} avatarUrl={previewAvatar} guest />
                    <div><strong>{selectedGuestAvatar?.label ?? ui(`Random ${talkBrand.productName} identity`, "Случайная фигура")}</strong><span>{ui("Ready for this room", "Готово для этой комнаты")}</span></div>
                    <button type="button" aria-expanded={guestAvatarOpen} aria-controls="guest-avatar-options" onClick={() => setGuestAvatarOpen((open) => !open)}>{guestAvatarOpen ? ui("Done", "Готово") : ui("Change avatar", "Сменить фигуру")}<ChevronDown className={guestAvatarOpen ? "is-open" : ""} aria-hidden="true" /></button>
                  </div>
                  {guestAvatarOpen ? <fieldset id="guest-avatar-options" className="guest-avatar-picker">
                    <legend><span>{ui("Choose guest identity", "Выберите фигуру гостя")}</span><small>{selectedGuestAvatar?.label ?? ui(`Approved ${talkBrand.productName} identity`, "Фигура из набора")}</small></legend>
                    <div className="guest-avatar-picker__grid">
                      {talkRoster.map((asset) => {
                        const selected = asset.key === guestIdentity.assetKey;
                        const avatarUrl = rosterAsset(asset.key, effectiveHaramMode) ?? (effectiveHaramMode ? asset.hijabAvatarUrl : asset.avatarUrl);
                        return <label className={`guest-avatar-option${selected ? " is-selected" : ""}`} key={asset.key} title={asset.label}><input type="radio" name="guest-avatar" value={asset.key} checked={selected} onChange={() => updateGuestIdentity({ assetKey: asset.key })} /><img src={avatarUrl} alt="" width="54" height="54" loading="lazy" decoding="async" /><span className="sr-only">{asset.label}</span>{selected ? <Check aria-hidden="true" /> : null}</label>;
                      })}
                    </div>
                    <p>{ui("Your choice is used only for this room and remains fixed if you reconnect.", "Выбор действует только в этой комнате и сохранится, если вы переподключитесь.")}</p>
                  </fieldset> : null}
                </div>
              ) : null}
              {talkBrand.supportsHaramMode ? <button
                className={`prejoin-haram-toggle${haramMode ? " is-active" : ""}`}
                type="button"
                role="switch"
                aria-checked={haramMode}
                onClick={() => updateHaramMode(!haramMode)}
              >
                <span className="prejoin-haram-toggle__icon"><EyeOff aria-hidden="true" /></span>
                <span><strong>Haram mode</strong><small>{room.haramModeDefault ? "This room starts with modest hijab avatars. Change it only for your view; live video is unchanged." : "This room starts with standard avatars. Turn on modest hijab artwork only for your view; live video is unchanged."}</small></span>
                <b>{haramMode ? "On" : "Off"}</b>
              </button> : null}
              <div className="device-fields">
                <label className="field"><span>{ui("Microphone", "Микрофон")}</span><select value={device.preferences.microphoneDeviceId} onChange={(event) => device.update({ microphoneDeviceId: event.target.value }, true)}><option value="">{ui("System default", "Как в системе")}</option>{device.devices.microphones.map((item, index) => <option key={item.deviceId} value={item.deviceId}>{item.label || ui(`Microphone ${index + 1}`, `Микрофон ${index + 1}`)}</option>)}</select></label>
                <label className="field"><span>{ui("Camera", "Камера")}</span><select value={device.preferences.cameraDeviceId} onChange={(event) => device.update({ cameraDeviceId: event.target.value }, true)}><option value="">{ui("System default", "Как в системе")}</option>{device.devices.cameras.map((item, index) => <option key={item.deviceId} value={item.deviceId}>{item.label || ui(`Camera ${index + 1}`, `Камера ${index + 1}`)}</option>)}</select></label>
                <label className="field"><span>{ui("Speaker", "Динамик")}</span><div className="select-action"><select value={device.preferences.speakerDeviceId} onChange={(event) => device.update({ speakerDeviceId: event.target.value })}><option value="">{ui("System default", "Как в системе")}</option>{device.devices.speakers.map((item, index) => <option key={item.deviceId} value={item.deviceId}>{item.label || ui(`Speaker ${index + 1}`, `Динамик ${index + 1}`)}</option>)}</select><button type="button" aria-label={ui("Test speaker", "Проверить динамик")} onClick={() => void testSpeaker()}><Headphones aria-hidden="true" size={18} /></button></div></label>
              </div>
              <div className={`network-check network-check--${device.network}`}><NetworkIcon aria-hidden="true" size={20} /><div><strong>{network.label}</strong><span>{network.detail}</span></div>{device.network === "good" ? <Check aria-hidden="true" size={18} /> : <RefreshCw aria-hidden="true" size={18} />}</div>
              {room.mode === "private_1to1" ? <StatusNotice tone="info" title={ui("Private 1:1 with mandatory E2EE", "Личный звонок с обязательным сквозным шифрованием")}><LockKeyhole aria-hidden="true" size={14} /> {ui(`You will compare a seven-emoji security code with the other person. Direct mode reveals your network address to that person; encrypted relay fallback does not expose media to ${talkBrand.productName}.`, "Вы сравните код из семи эмодзи с собеседником. В прямом режиме собеседник видит ваш сетевой адрес; запасной зашифрованный канал не отдаёт видео и звук серверу.")}</StatusNotice> : null}
              {joinError ? <StatusNotice tone="danger" title={ui("Could not join", "Не удалось войти")}>{shownMessage(joinError)}</StatusNotice> : null}
              <button className="button button--primary button--join" type="button" disabled={joining || !displayName.trim() || device.network === "offline" || room.state === "ended"} onClick={() => void join()}>{joining ? <LoaderCircle className="spin" aria-hidden="true" size={19} /> : null}{joining ? ui("Joining room…", "Входим в комнату…") : room.state === "ended" ? ui("Room has ended", "Комната завершена") : ui("Join room", "Войти в комнату")}</button>
            </>
          ) : null}
        </section>
      </main>
    </div>
  );
}
